import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { ChevronRight, FileText, Pause, Play, Trash2, Calendar, Pencil } from 'lucide-react'
import { TriggerAttribution } from '@/components/TriggerAttribution'
import {
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { testRunsAPI, testSuitesAPI, variantsAPI, scheduledRunsAPI, replayTemplatesAPI, runTemplatesAPI } from '@/lib/api'
import { formatDate, formatDateTime, formatDuration, formatRelativeTime } from '@/lib/utils'
import { useAppContext } from '@/hooks/useAppContext'
import { useLiveRun, describeCurrentTest } from '@/hooks/useLiveRun'
import { RunStartBar } from '@/components/RunStartBar'
import { RunSummaryStats } from '@/components/RunSummaryStats'
import { useManualRunTemplates } from '@/hooks/useManualRunTemplates'
import { TestAuthoringTemplateDialog } from '@/components/TestAuthoringTemplateDialog'
import { ReplayTemplateDialog } from '@/components/ReplayTemplateDialog'
import { ResumeTemplateDialog } from '@/components/ResumeTemplateDialog'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { ActivityLog } from '@/components/ActivityLog'
import { ResizablePanels } from '@/components/ResizablePanels'
import { RunResultRows, statusIcon } from '@/components/RunResultRows'
import type { TestRun, TestSuite, ReplayTemplate } from '@/types'

export default function Results() {
  const { selectedAppId, selectedVariantId } = useAppContext()

  /** #2158 slice 3a — this page can start a run now, so it owns one.
   * `useLiveRun` is the same hook the runner page uses; the socket and the
   * run-state reducer are not duplicated here. */
  const liveRun = useLiveRun()

  /** The variant's run settings for runs a person starts, one per kind.
   *
   * Three separate buttons, so a single default across every kind would let
   * all three start the same kind of run. `useManualRunTemplates` owns the
   * resolving, including asking only for the manual pool; this page just
   * needs the rows, because pressing a button opens that kind's settings
   * and the dialog needs the values. */
  const { byKind: templatesByKind, reload: loadTemplates } = useManualRunTemplates(selectedVariantId)

  /** The saved settings per kind, whole (#2225). This used to reduce each
   * row to its id; the bar needs the row to say which stages the button
   * will run. */
  const defaultsByKind = templatesByKind

  /** Which kind's settings are open, or null. Pressing Run, Resume or
   * Create Tests opens the settings rather than starting (#2175 slice 2) —
   * a button that silently starts a run gives the operator nowhere to see
   * what it is about to do. */
  const [openKind, setOpenKind] = useState<'generation' | 'resume' | 'replay' | null>(null)

  /** Reattach to a run already in flight, so landing here mid-run shows it
   * rather than an idle page.
   *
   * `?run=` is set by the buttons on the Tests page, which know the id of
   * the run they just started. Without it this attaches to whichever run
   * the active list returns first, so with two going it could show the
   * other one (#2206). */
  const [searchParams] = useSearchParams()
  const wantedRunId = searchParams.get('run') ?? undefined
  useEffect(() => {
    void liveRun.resume(selectedAppId, selectedVariantId, wantedRunId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, [])

  /** Select a run once it finishes, so the pane below answers what it
   * produced (#2225).
   *
   * Keyed on the run id rather than a status transition: a page opened with
   * `?run=` on a run that ALREADY finished must select it too, and that
   * never transitions — it arrives terminal (#2206). Selecting the same id
   * twice is a no-op, so the effect is safe to re-enter.
   *
   * It lives here rather than in `useLiveRun` because selection is this
   * page's business; the hook is shared and deliberately owns no rendering.
   */
  const finishedRunId =
    liveRun.run && liveRun.run.status !== 'running' ? liveRun.run.id : null
  useEffect(() => {
    if (!finishedRunId) return
    let cancelled = false
    void (async () => {
      await loadData()
      if (!cancelled) setSelectedRunId(finishedRunId)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on the id only
  }, [finishedRunId])

  /** Start a run from a kind's settings. `settingsOverride` present means
   * the operator changed something in the dialog and pressed Run: the run
   * uses those values and the saved ones are left alone. */
  async function startKind(
    kind: 'generation' | 'resume' | 'replay',
    label: string,
    settingsOverride?: Record<string, unknown>,
  ) {
    const templateId = defaultsByKind[kind]?.id
    if (!templateId) return
    setOpenKind(null)
    // A new run starts focused: collapse the past-runs list again even if it
    // was expanded while watching a previous run (#2304).
    setHistoryExpanded(false)
    const runId = await liveRun.start(templateId, {
      label,
      applicationId: selectedAppId,
      variantId: selectedVariantId,
      ...(settingsOverride ? { settingsOverride } : {}),
    })
    // The list refreshes so the new run appears in it. Selecting it is NOT
    // done here (#2225): a run that has only just started has nothing to
    // show, and selecting it puts an empty detail pane beside a card that
    // is already reporting. The effect below selects it once it finishes.
    if (runId) await loadData()
  }
  const wp = useWorkspacePath()
  const navigate = useNavigate()
  const tz = useUserTimezone()
  const [tab, setTab] = useState<'completed' | 'scheduled'>('completed')
  const [variantNames, setVariantNames] = useState<Record<string, string>>({})
  const [runs, setRuns] = useState<TestRun[]>([])
  const [suites, setSuites] = useState<TestSuite[]>([])
  const [schedules, setSchedules] = useState<any[]>([])
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  /** Whether the operator expanded the past-runs list while a run is on the
   * card (#2304). Collapsed by default whenever a current run is showing; a
   * click opens the two-pane browser without dismissing the run. Reset on
   * Dismiss so the next run starts collapsed again. */
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; onConfirm: () => void } | null>(null)
  const [editingSchedule, setEditingSchedule] = useState<any>(null)
  // The scheduler-pool run-setting the schedule being edited owns (#2391).
  // Loaded when Edit is pressed so the dialog can seed its Settings tab.
  const [editingScheduleTemplate, setEditingScheduleTemplate] = useState<any>(null)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    const [runsResponse, suitesData, schedulesData] = await Promise.all([
      testRunsAPI.getAll(),
      testSuitesAPI.getAll(),
      scheduledRunsAPI.getAll(),
    ])
    setRuns(runsResponse.runs)
    setSuites(suitesData)
    setSchedules(schedulesData)
  }

  const filteredRuns = selectedAppId
    ? runs.filter((r) => r.applicationId === selectedAppId)
    : runs

  const selectedRun = runs.find((r) => r.id === selectedRunId)

  /** While a run is on the card, the past-runs list collapses to a strip so the
   * current run stays the focus (#2304). Expanding it, or dismissing the run,
   * restores the two-pane browser. Only the completed tab collapses — the
   * scheduled tab is not about runs. */
  const collapseCompleted = tab === 'completed' && !!liveRun.run && !historyExpanded

  function getVariantName(variantId: string): string {
    return variantNames[variantId] ?? 'Unknown'
  }

  useEffect(() => {
    if (selectedAppId) {
      variantsAPI.getByApp(selectedAppId).then((variants) => {
        const names: Record<string, string> = {}
        for (const v of variants) names[v.id] = v.name
        setVariantNames(names)
      })
    }
  }, [selectedAppId])

  function getRunLabel(run: TestRun): string {
    if (run.testSuiteId) {
      return suites.find((s) => s.id === run.testSuiteId)?.name ?? 'Test Suite Run'
    }
    return 'AI Discovery'
  }

  // Run/test status icons come from `statusIcon` in RunResultRows now, so the
  // card rows, the pane rows, and the run-list all read a status the same way
  // from one place (#2304).

  function getRunStatus(run: TestRun): 'passed' | 'failed' | 'running' | 'unknown' {
    if (run.status === 'running') return 'running'
    if (run.status === 'completed') {
      return run.summary && run.summary.failed > 0 ? 'failed' : 'passed'
    }
    return 'unknown'
  }

  const appVariantIds = new Set(Object.keys(variantNames))
  const filteredSchedules = selectedAppId
    ? schedules.filter((s: any) => appVariantIds.has(s.variantId))
    : schedules

  async function toggleSchedulePause(id: string) {
    await scheduledRunsAPI.togglePause(id)
    const updated = await scheduledRunsAPI.getAll()
    setSchedules(updated)
  }

  function deleteSchedule(s: any) {
    setConfirmDialog({
      message: 'Delete this schedule?',
      onConfirm: async () => {
        await scheduledRunsAPI.delete(s._id)
        // Each schedule owns its scheduler-pool run-setting (#2391); remove it
        // too so unused settings don't pile up. Guard against a shared row
        // (defensive — the create flow makes one per schedule), and treat a
        // failed delete as benign: an unused run-setting never runs on its own.
        const others = schedules.filter((x: any) => x._id !== s._id && x.templateId === s.templateId)
        if (s.templateId && others.length === 0) {
          await replayTemplatesAPI.delete(s.templateId).catch(() => {})
        }
        const updated = await scheduledRunsAPI.getAll()
        setSchedules(updated)
      },
    })
  }

  /** Edit a schedule (#2391): load the scheduler-pool run-setting it owns so
   * the dialog can seed its Settings tab, then open the Run Suite dialog in
   * edit-schedule mode (settings + timetable). */
  async function openEditSchedule(s: any) {
    try {
      const rows = await runTemplatesAPI.listForVariant(s.variantId, 'scheduler') as any[]
      const tmpl = rows.find(r => r.id === s.templateId)
      if (!tmpl) return // no owning run-setting to edit (should not happen for a schedule made here)
      setEditingScheduleTemplate(tmpl)
      setEditingSchedule(s)
    } catch {
      // A failed load leaves the dialog closed rather than opening it empty.
    }
  }

  function describeFrequency(s: any): string {
    // Schedule has its OWN timezone (what tz the schedule runs in —
    // distinct from the user's display tz). Shadow naming the local
    // is fine; we only use this for the human-readable suffix.
    const scheduleTz = (s.timezone || '').replace(/_/g, ' ')
    switch (s.frequency) {
      case 'once': return `Once on ${formatDate(s.startDate, tz)}`
      case 'daily': return `Daily · ${scheduleTz}`
      case 'weekly': {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        const dayList = (s.daysOfWeek || []).map((d: number) => days[d]).join(', ')
        return `Weekly (${dayList}) · ${scheduleTz}`
      }
      case 'cron': return `${s.cronExpression} · ${scheduleTz}`
      default: return s.frequency
    }
  }

  const tabClass = (t: string) =>
    `px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)]">
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Runs</h1>
          <p className="text-sm text-muted-foreground">
            Review past test run results and schedules
          </p>
        </div>
        <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
          <button className={tabClass('completed')} onClick={() => setTab('completed')}>
            Completed{filteredRuns.length > 0 ? ` (${filteredRuns.length})` : ''}
          </button>
          <button className={tabClass('scheduled')} onClick={() => setTab('scheduled')}>
            Scheduled{filteredSchedules.length > 0 ? ` (${filteredSchedules.length})` : ''}
          </button>
        </div>
      </div>
      {/* #2158 slice 3a — starting a run happens here now, above the list so
          the page reads top to bottom as start → watch → review. The bar
          itself is a fixed strip: it is three buttons, it does not grow, and
          it should not scroll away from the thing it starts. */}
      <div className="px-6 pb-3">
        <RunStartBar
          defaultsByKind={defaultsByKind}
          busy={liveRun.starting || liveRun.run?.status === 'running'}
          onStart={(kind) => setOpenKind(kind)}
          settingsHref={wp('/applications')}
        />
      </div>

      {/* The live run and the list SHARE what is left of the viewport
          (#2206). The card used to sit above this boundary in a block that
          neither shrank nor scrolled, so a run's log — around 490px of it —
          pushed the list down to a sliver. The list scrolls internally, so
          the page itself did not scroll and the rows below the fold could
          not be reached at all. Worst right after starting a run, which is
          exactly when a test button sends you here.

          The row scrolls, the card takes its natural height, and the list
          has a floor. On a tall window nothing scrolls here at all; on a
          short one the row does, so both are always reachable. The card is
          deliberately NOT capped with its own scrollbar — it already has
          one inside the log, and two nested scrollbars in one card is a
          worse answer than scrolling the row. */}
      <div className="flex-1 min-h-0 px-6 pb-6 flex flex-col gap-3 overflow-y-auto">
        {liveRun.run && (
          <div data-testid="live-run" className="flex-none rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm font-medium">
                {liveRun.run.status === 'running'
                  ? `Running${liveRun.currentTest
                    ? ` — ${describeCurrentTest(liveRun.currentTest)}`
                    : liveRun.phase ? ` — ${liveRun.phase}` : ''}…`
                  : `Run ${liveRun.run.status}`}
              </p>
              <div className="flex items-center gap-2">
                {liveRun.run.status === 'running' && (
                  <Button variant="destructive" size="sm" onClick={() => { void liveRun.stop() }}>
                    Stop
                  </Button>
                )}
                {/* #2304 — the card is the single detail surface for the current
                    run, so its own report button lives here again, shown once
                    there are tests to report. Supersedes #2225, which moved it
                    to the pane to avoid two buttons; the pane now collapses
                    beside the card, so there is one button, and it is here. */}
                {liveRun.run.status !== 'running' && liveRun.run.summary && liveRun.run.summary.total > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(wp(`/runs/${liveRun.run!.id}/report`))}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Detailed test results
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => { setHistoryExpanded(false); liveRun.reset(); void loadData() }}>
                  Dismiss
                </Button>
              </div>
            </div>
            {/* What it produced, filling in as tests land (#2225). The bar
                below cannot tell you this: it tracks phase advancement, not
                pass and fail. The figures stay after the run ends — hiding
                them then would take the number away at the moment it
                becomes final. */}
            {liveRun.run.summary && <RunSummaryStats summary={liveRun.run.summary} />}
            {/* The backend's own percentage — it tracks per-phase advancement,
                which a client-side count of finished tests cannot. */}
            <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${liveRun.progressPct}%` }} />
            </div>
            {/* #2304 — per-test outcomes on the card as they land, so the
                current run's detail lives here rather than only in the pane.
                Same rows the past-run detail renders (RunResultRows). */}
            <RunResultRows results={liveRun.run.results} />
            <ActivityLog logs={liveRun.run.logs} isRunning={liveRun.run.status === 'running'} phase={liveRun.phase ?? undefined} />
          </div>
        )}

        <div data-testid="run-list" className={collapseCompleted ? 'flex-none' : 'flex-1 min-h-[14rem]'}>
        {tab === 'scheduled' ? (
          <div className="h-full border rounded-lg overflow-auto p-4 space-y-6">
            <div className="space-y-2">
            {filteredSchedules.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Calendar className="h-10 w-10 mb-3" />
                <p className="text-sm">No scheduled runs</p>
                <p className="text-xs mt-1">Open a suite in Tests and press Run Suite → When → Schedule to add one.</p>
              </div>
            ) : (
              filteredSchedules.map((s: any) => (
                <div key={s._id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{s.templateName}</span>
                      <Badge variant={s.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {s.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {getVariantName(s.variantId)} · {describeFrequency(s)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.nextRunAt ? `Next: ${formatDateTime(s.nextRunAt, 'absoluteUTC')}` : 'No next run'}
                      {s.lastRunAt && ` · Last: ${formatRelativeTime(s.lastRunAt)}`}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon-sm" onClick={() => { void openEditSchedule(s) }} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => toggleSchedulePause(s._id)} title={s.status === 'active' ? 'Pause' : 'Resume'}>
                      {s.status === 'active' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => deleteSchedule(s)} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
            </div>
          </div>
        ) : collapseCompleted ? (
          /* #2304 — a run is on the card, so the past-runs list is a strip
             that keeps the focus on the current run. Clicking it opens the
             two-pane browser without dismissing the run; Dismiss (or no run)
             expands it back on its own. */
          <button
            type="button"
            onClick={() => setHistoryExpanded(true)}
            className="w-full flex items-center gap-2 rounded-lg border bg-card px-4 py-3 text-left hover:bg-muted/50 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium text-sm">
              Past runs{filteredRuns.length > 0 ? ` (${filteredRuns.length})` : ''}
            </span>
            <span className="text-xs text-muted-foreground ml-1">Click to browse earlier runs</span>
          </button>
        ) : (
        <div className="h-full border rounded-lg overflow-hidden">
          <ResizablePanels
            defaultWidth={320}
            minWidth={240}
            maxWidth={450}
            left={
              <div className="flex flex-col h-full">
                {/* The run count used to sit here and the tab above already
                    says "Completed (10)". Two counts of the same thing, and
                    neither is the one you came for (#2194). */}
                <div className="flex-1 overflow-y-auto">
                  {/* #2158 slice 3a — the empty case used to be a whole
                      separate layout returned before the start bar existed,
                      so the one state that most needs the start controls
                      rendered without them and told the operator to "start one
                      above" with nothing above it. Now it is a row in the list
                      it belongs to, and the bar is always present.
                      (Codex review on PR #2168.) */}
                  {filteredRuns.length === 0 && (
                    <p className="p-4 text-sm text-muted-foreground text-center">
                      No runs yet. Start one above and it will appear here.
                    </p>
                  )}
                  {filteredRuns.map((run) => (
                    <button
                      key={run.id}
                      onClick={() => setSelectedRunId(run.id)}
                      className={`w-full p-3 text-left border-b border-border/50 transition-colors ${
                        selectedRunId === run.id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {statusIcon(getRunStatus(run))}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{getRunLabel(run)}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {getVariantName(run.variantId)}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatRelativeTime(run.startedAt)}
                        </span>
                      </div>
                      {run.summary && (
                        <div className="mt-1.5 flex gap-2 text-xs pl-6">
                          <span className="text-success">{run.summary.passed} passed</span>
                          {run.summary.failed > 0 && (
                            <span className="text-destructive">{run.summary.failed} failed</span>
                          )}
                        </div>
                      )}
                      {/* Run-history attribution (#1134). Shown only for
                          Option A runs fired by a TriggerRule — heuristic:
                          buildMetadata present with repoIdentifier. Manual
                          and Option-B CI-YAML runs don't have buildMetadata. */}
                      <TriggerAttribution buildMetadata={run.buildMetadata} />
                    </button>
                  ))}
                </div>
              </div>
            }
            right={
              !selectedRun || (liveRun.run && selectedRun.id === liveRun.run.id) ? (
                /* Deliberately empty. Two reasons land here (#2194, #2304):
                   no run is selected — "Select a run to view details" told the
                   operator to do the thing they were already doing; OR the
                   selected run IS the one on the current-run card, whose detail
                   already lives there, so showing it again would be two panes
                   for one run. Other past runs open here normally. */
                <div className="h-full" />
              ) : (
                <div className="overflow-y-auto h-full p-4 space-y-4">
                  {/* Run Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-base font-semibold">{getRunLabel(selectedRun)}</h2>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {getVariantName(selectedRun.variantId)} · {formatDateTime(selectedRun.startedAt, 'absoluteUTC')}
                        {selectedRun.duration && ` · ${formatDuration(selectedRun.duration)}`}
                      </p>
                    </div>
                    {selectedRun.summary && selectedRun.summary.total > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        // Routes to RunReport.tsx, which iframes the
                        // Playwright HTML report — the rich per-test
                        // detail surface (steps with trace timeline,
                        // before/after DOM snapshots, errors, network
                        // log, screenshots, videos). Replaces the
                        // chevron-expansion panel that used to live
                        // on each test row but never had its data
                        // pipeline wired (executor.ts hardcodes
                        // `steps: []` on streaming results), so every
                        // expansion fell through to "No details
                        // available." The Playwright HTML report is
                        // the strictly-richer source that already
                        // worked end-to-end.
                        onClick={() => navigate(wp(`/runs/${selectedRun.id}/report`))}
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        Detailed test results
                      </Button>
                    )}
                  </div>

                  {selectedRun.summary && <RunSummaryStats summary={selectedRun.summary} />}

                  {/* Results List — single-line rows, the same ones the
                      current-run card renders (RunResultRows, #2304). The
                      richer per-test detail (steps, trace, screenshots) is the
                      "Detailed test results" report linked above; the streaming
                      executor only emits {title, status, duration}, so these
                      rows are the outcome, not an expandable trace. */}
                  <RunResultRows results={selectedRun.results} />

                  {/* Activity Log */}
                  <ActivityLog runId={selectedRun.id} isRunning={selectedRun.status === 'running'} />
                </div>
              )
            }
          />
        </div>
        )}
        </div>
      </div>

      {editingSchedule && editingScheduleTemplate && (
        <ReplayTemplateDialog
          template={editingScheduleTemplate}
          variantId={editingSchedule.variantId}
          applicationId={selectedAppId}
          suites={suites}
          schedulable
          editingSchedule={editingSchedule}
          onClose={() => {
            setEditingSchedule(null)
            setEditingScheduleTemplate(null)
          }}
          onSaved={() => { scheduledRunsAPI.getAll().then(setSchedules) }}
        />
      )}

      {/* The settings behind each button (#2175 slice 2). Same dialogs the
          settings page uses — one editor per kind, not a second one that
          would drift from it. The launch prop is what swaps their footer
          for Run / Save and run. */}
      {openKind === 'generation' && selectedVariantId && templatesByKind.generation && (
        <TestAuthoringTemplateDialog
          template={templatesByKind.generation}
          allTemplates={[]}
          appId={selectedAppId}
          variantId={selectedVariantId}
          onClose={() => setOpenKind(null)}
          onSaved={() => { void loadTemplates() }}
          launch={(settings) => startKind('generation', 'AI discovery', settings)}
        />
      )}
      {openKind === 'resume' && selectedVariantId && templatesByKind.resume && (
        <ResumeTemplateDialog
          template={templatesByKind.resume}
          variantId={selectedVariantId}
          applicationId={selectedAppId}
          onClose={() => setOpenKind(null)}
          onSaved={() => { void loadTemplates() }}
          launch={(settings) => startKind('resume', 'resume incomplete work', settings)}
        />
      )}
      {openKind === 'replay' && selectedVariantId && templatesByKind.replay && (
        <ReplayTemplateDialog
          template={templatesByKind.replay as unknown as ReplayTemplate}
          variantId={selectedVariantId}
          applicationId={selectedAppId}
          suites={suites}
          onClose={() => setOpenKind(null)}
          onSaved={() => { void loadTemplates() }}
          launch={(settings) => startKind('replay', 'replay', settings)}
        />
      )}

      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">{confirmDialog?.message}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => { confirmDialog?.onConfirm(); setConfirmDialog(null) }}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
